import React from 'react';
import {
  FaMobileAlt,
  FaLaptopCode,
  FaServer,
  FaDatabase,
  FaCloud,
  FaInfinity,
  FaBrain,
  FaProjectDiagram
} from 'react-icons/fa';
import Section from './layout/Section';
import ScrollReveal from './animations/ScrollReveal';
import SpotlightCard from './effects/SpotlightCard';
import SignalBars from './effects/SignalBars';
import TerminalFrame from './effects/TerminalFrame';
import CodeRain from './effects/CodeRain';
import { skillGroups } from '../data/profile';

const groupStyles = {
  mobile: { icon: FaMobileAlt, accent: 'from-sky-500/20 to-sky-500/0 text-sky-500' },
  frontend: { icon: FaLaptopCode, accent: 'from-blue-500/20 to-blue-500/0 text-blue-500' },
  backend: { icon: FaServer, accent: 'from-emerald-500/20 to-emerald-500/0 text-emerald-500' },
  databases: { icon: FaDatabase, accent: 'from-amber-500/20 to-amber-500/0 text-amber-500' },
  cloud: { icon: FaCloud, accent: 'from-indigo-500/20 to-indigo-500/0 text-indigo-500' },
  devops: { icon: FaInfinity, accent: 'from-violet-500/20 to-violet-500/0 text-violet-500' },
  ai: { icon: FaBrain, accent: 'from-fuchsia-500/20 to-fuchsia-500/0 text-fuchsia-500' },
  architecture: { icon: FaProjectDiagram, accent: 'from-rose-500/20 to-rose-500/0 text-rose-500' }
};

const signalLevels = [
  { label: 'mobile', value: 95 },
  { label: 'frontend', value: 90 },
  { label: 'backend', value: 88 },
  { label: 'ai_ml_rag', value: 78 },
  { label: 'cloud_devops', value: 82 },
  { label: 'architecture', value: 86 }
];

export default function Skills() {
  return (
    <Section
      id="skills"
      eyebrow="Skills"
      title="The toolkit"
      lead="Grouped by what they’re for. These are the tools I reach for most, not everything I’ve ever touched."
      className="relative border-y border-slate-200/80 bg-white dark:border-emerald-500/10 dark:bg-slate-950"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <CodeRain opacity={0.12} density={12} />
      </div>

      <div className="mb-10 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.15fr]">
        <ScrollReveal direction="up">
          <TerminalFrame title="sysinfo · capability_map" className="h-full">
            <p className="mb-4 text-slate-500 dark:text-emerald-500/70">
              <span className="text-emerald-500">$</span> ./probe --stack --live
            </p>
            <SignalBars items={signalLevels} />
            <p className="mt-5 text-[10px] uppercase tracking-[0.16em] text-slate-400">
              signal strength · relative proficiency
            </p>
          </TerminalFrame>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={0.08}>
          <TerminalFrame title="boot.log" className="h-full" bodyClassName="space-y-1.5">
            {[
              ['OK', 'flutter runtime mounted'],
              ['OK', 'react + node services online'],
              ['OK', 'cloud targets authenticated'],
              ['RUN', 'langchain / rag modules loading'],
              ['OK', 'ci pipeline green']
            ].map(([status, line]) => (
              <p key={line} className="flex gap-3">
                <span
                  className={
                    status === 'RUN'
                      ? 'text-amber-500'
                      : 'text-emerald-600 dark:text-emerald-400'
                  }
                >
                  [{status}]
                </span>
                <span>{line}</span>
              </p>
            ))}
            <p className="pt-2 text-emerald-500">
              ready<span className="animate-caret">_</span>
            </p>
          </TerminalFrame>
        </ScrollReveal>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {skillGroups.map((group, index) => {
          const style = groupStyles[group.key] ?? groupStyles.frontend;
          const Icon = style.icon;

          return (
            <ScrollReveal key={group.key} direction="up" delay={(index % 4) * 0.06} className="h-full">
              <SpotlightCard className="h-full p-6">
                <span
                  className={`mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br
                              ring-1 ring-inset ring-slate-200 transition-transform duration-500
                              ease-smooth group-hover:scale-110 dark:ring-white/10 ${style.accent}`}
                >
                  <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>

                <h3 className="font-mono text-[13px] font-semibold uppercase tracking-wider text-slate-900 dark:text-white">
                  ./{group.name.toLowerCase()}
                </h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
                  {group.blurb}
                </p>

                <ul className="mt-4 flex flex-wrap gap-1.5">
                  {group.skills.map((skill) => (
                    <li
                      key={skill}
                      className="rounded-full border border-slate-200 bg-slate-50/80 px-2.5 py-1 text-[11.5px]
                                 font-medium text-slate-600 transition-colors duration-300
                                 group-hover:border-emerald-500/30 dark:border-white/[0.08] dark:bg-white/[0.03]
                                 dark:text-slate-300 dark:group-hover:border-emerald-400/25"
                    >
                      {skill}
                    </li>
                  ))}
                </ul>
              </SpotlightCard>
            </ScrollReveal>
          );
        })}
      </div>
    </Section>
  );
}
